import React, { useState } from "react"
import styled, { keyframes } from "styled-components"
import { MobergFontFamily } from "../MobergFont/MobergFont"

// The sources behind a document-backed answer.
//
// This is the component that makes Clara worth using over a general assistant. Copilot
// told a nurse five times that it did not have her hospital's policy; our answer is only
// better if she can see exactly where it came from and go and check it. A citation she
// cannot verify is worth less than none, because it looks authoritative.
//
// Each card shows only its document and location until asked. The answer is what she
// came for; several open passages bury it. The evidence stays one click away, never
// gone, and never in the way.

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`

const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to   { opacity: 1; transform: translateY(0); }
`

// A policy number is the thing a nurse types into the policy library, so it earns the
// badge slot. Without one we fall back to the document type.
function badgeFor(source) {
	if (source.doc_number) {
		const [first] = String(source.doc_number).split(/[\s.]/)
		return first.slice(0, 4).toUpperCase()
	}
	return "DOC"
}

// The site's own classification from the upload menu (Validated Policy, Guideline...).
// Shown so a reader can see at a glance what kind of authority stands behind a quote;
// absent entirely for documents the site has not classified.
function locationOf(source) {
	const parts = []
	if (source.section) parts.push(source.section)
	if (source.page_start) parts.push(`page ${source.page_start}`)
	return parts.join(" · ")
}

export function ContextCards({ sources = [], label = "Sources" }) {
	// Collapsed by default. The answer is what the clinician came for; the passage is
	// there to be checked when she wants to check it, and several open passages bury
	// the answer she just read. One at a time, so opening a second closes the first and
	// the chat never grows unexpectedly under the reader.
	const [openId, setOpenId] = useState(null)

	if (!sources.length) return null

	return (
		<Wrap>
			<Head>
				<HeadLabel>{label}</HeadLabel>
				<Count>{sources.length}</Count>
			</Head>

			{sources.map((source, index) => {
				const id = source.chunk_id ?? index
				const open = openId === id
				return (
					<Card key={id} $delay={index * 90} $open={open}>
						<Bar type="button" $open={open} aria-expanded={open} onClick={() => setOpenId(open ? null : id)}>
							<Lines viewBox="0 0 24 24" aria-hidden>
								<path d="M4 6h16M4 12h16M4 18h10" />
							</Lines>
							<TitleText title={source.document_title}>{source.document_title}</TitleText>
							{source.review_status === "overdue" && (
								<OverdueTag>
									{source.next_review_date ? `Review overdue · due ${reviewDate(source.next_review_date)}` : "Review overdue"}
								</OverdueTag>
							)}
							<Where>{locationOf(source)}</Where>
							<Chevron viewBox="0 0 24 24" $open={open} aria-hidden>
								<path d="M6 9l6 6 6-6" />
							</Chevron>
						</Bar>

						<Collapse $open={open}>
							<CollapseInner>
								<Passage>{source.text}</Passage>
								<ChipRow>
									<Chip
										as={source.source_url ? "a" : "span"}
										href={source.source_url || undefined}
										target={source.source_url ? "_blank" : undefined}
										rel={source.source_url ? "noreferrer" : undefined}
										$link={Boolean(source.source_url)}
									>
										<Badge>{badgeFor(source)}</Badge>
										{source.doc_number || source.document_title}
										{source.document_type && <TypeTag>{source.document_type}</TypeTag>}
										{source.source_url && (
											<Arrow viewBox="0 0 24 24" aria-hidden>
												<path d="M7 17L17 7M7 7h10v10" />
											</Arrow>
										)}
									</Chip>
								</ChipRow>
							</CollapseInner>
						</Collapse>
					</Card>
				)
			})}
		</Wrap>
	)
}

const Wrap = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 12px;
	min-width: 0;
`

const Head = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 2px;
	animation: ${fadeIn} 400ms ease-out both;
`

const HeadLabel = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 600;
	color: var(--text-color);
`

const Count = styled.span`
	display: inline-flex;
	align-items: center;
	height: 19px;
	padding: 0 7px;
	border-radius: 6px;
	background: var(--color-hover-neutral);
	border: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11.5px;
	font-weight: 500;
	font-variant-numeric: tabular-nums;
	color: var(--text-secondary-color);
`

const Card = styled.div`
	overflow: hidden;
	border-radius: 10px;
	background: var(--color-surface-panel);
	border: 1px solid var(--color-border-light);
	animation: ${fadeUp} 400ms cubic-bezier(0.23, 1, 0.32, 1) ${({ $delay }) => $delay}ms both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

const Bar = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 9px 12px;
	background: none;
	border: none;
	/* The divider only exists when the body is showing, so a collapsed card reads as a
	   single row rather than an empty box with a line under its heading. */
	border-bottom: 1px solid ${({ $open }) => ($open ? "var(--color-border-light)" : "transparent")};
	text-align: left;
	cursor: pointer;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 500;
	color: var(--text-color);
	transition: background-color 130ms ease, border-color 200ms ease;

	&:hover {
		background: var(--color-hover-neutral);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: -2px;
	}
`

const Lines = styled.svg`
	width: 11px;
	height: 11px;
	flex-shrink: 0;
	fill: none;
	stroke: currentColor;
	stroke-width: 2.5;
	stroke-linecap: round;
`

const TitleText = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const Where = styled.span`
	margin-left: auto;
	flex-shrink: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
	font-variant-numeric: tabular-nums;
`

// Once opened, the document's own words in full, never truncated. A nurse deciding
// whether to trust an answer needs the whole sentence, not a preview of it.
const Passage = styled.p`
	margin: 0;
	padding: 10px 12px 6px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	line-height: 1.6;
	color: var(--text-secondary-color);
`

const ChipRow = styled.div`
	padding: 0 12px 12px;
`

const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 24px;
	padding: 0 8px;
	border-radius: 999px;
	background: var(--color-hover-neutral);
	border: 1px solid var(--color-border-light);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 500;
	color: var(--text-secondary-color);
	text-decoration: none;
	transition: background-color 150ms ease, color 150ms ease;
	cursor: ${({ $link }) => ($link ? "pointer" : "default")};

	&:hover {
		background: ${({ $link }) => ($link ? "var(--color-hover-active-neutral)" : "var(--color-hover-neutral)")};
		color: ${({ $link }) => ($link ? "var(--text-color)" : "var(--text-secondary-color)")};
	}

`

const Badge = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 15px;
	height: 15px;
	padding: 0 3px;
	border-radius: 4px;
	background: var(--color-accent-interactive);
	color: #fff;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 8px;
	font-weight: 700;
	letter-spacing: 0.2px;
`

const TypeTag = styled.span`
	margin-left: 7px;
	padding: 1px 7px;
	border-radius: 999px;
	border: 1px solid var(--color-border-light);
	font-size: 10px;
	letter-spacing: 0.02em;
	color: var(--text-secondary-color);
	white-space: nowrap;
`

// A source past its review date is flagged on the card itself, next to the warning in the
// answer text (UTSW, Nitya 15 Sep). Filled, darker and slightly larger than the type tag:
// 10px of --color-danger-text on white measured about 2.9:1, under the 4.5:1 a warning
// needs, and the date it was due sat in a tooltip nobody hovers.
const OverdueTag = styled(TypeTag)`
	font-size: 11px;
	font-weight: 600;
	border-color: transparent;
	background: var(--color-danger-hover-bg, rgba(229, 115, 115, 0.12));
	color: var(--color-danger-text-hover, #d32f2f);
`

// "2026-03-31" -> "31 Mar 2026", the way Clara's answers write dates.
export function reviewDate(iso) {
	const [year, month, day] = String(iso).slice(0, 10).split("-").map(Number)
	if (!year || !month || !day) return String(iso)
	const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
	return `${day} ${names[month - 1]} ${year}`
}

const Arrow = styled.svg`
	width: 9px;
	height: 9px;
	flex-shrink: 0;
	fill: none;
	stroke: currentColor;
	stroke-width: 2.5;
	stroke-linecap: round;
	stroke-linejoin: round;
`

const Chevron = styled.svg`
	width: 13px;
	height: 13px;
	flex-shrink: 0;
	fill: none;
	stroke: var(--text-secondary-color);
	stroke-width: 2.2;
	stroke-linecap: round;
	stroke-linejoin: round;
	transition: transform 280ms cubic-bezier(0.23, 1, 0.32, 1);
	transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`

// grid-template-rows 0fr -> 1fr animates height without measuring the content, so a
// long passage cannot leave the card stuck at a stale height.
const Collapse = styled.div`
	display: grid;
	grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
	transition: grid-template-rows 320ms cubic-bezier(0.23, 1, 0.32, 1);

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const CollapseInner = styled.div`
	overflow: hidden;
`

export default ContextCards
